/**
 * `GET /v1/me` — sessão, tenant, permissões e feature flags (§6).
 *
 * ── Um tenant, não uma lista ──────────────────────────────────────────────
 *
 * A B14 foi escrita quando o modelo permitia a mesma conta em N revendas, e
 * previa que `/v1/me` listasse os tenants e que o ativo viesse de um header.
 *
 * A decisão **C3** mudou isso: licença por usuário, uma conta por empresa,
 * imposta pela migração 005. Uma conta autenticada tem exatamente **um**
 * vínculo, então não há o que escolher — e a armadilha nomeada na B14
 * ("aceitar o tenant de um header sem validar") deixa de existir por
 * construção, em vez de por cuidado.
 *
 * Quem atende duas revendas tem duas contas, uma por realm, e troca de uma para
 * a outra fazendo login de novo.
 */

import { z } from 'zod';
import { CarteiraSchema, IdSchema, PapelSchema } from './comum.js';
import { PermissaoDaSessaoSchema } from './permissoes.js';
import { PadroesDaAgendaSchema } from './consulta-da-oportunidade.js';

export const MeSchema = z
  .object({
    usuario: z.object({
      id: IdSchema,
      nome: z.string(),
      email: z.string(),
      /**
       * O link assinado da foto de perfil (061), ou nulo sem foto — e aí a
       * tela mostra as iniciais. Vale cinco minutos: quem guarda a resposta
       * por mais tempo precisa pedir `/v1/me` de novo.
       */
      foto_url: z.string().nullable(),
    }),

    tenant: z.object({
      id: IdSchema,
      slug: z.string(),
      nome: z.string(),
    }),

    /**
     * O papel de `core.memberships`, **não** o do token.
     *
     * O claim `papeis` existe para o gateway não consultar o banco a cada
     * requisição, mas ele congela no momento da emissão: pessoa rebaixada
     * continua com o papel antigo até o token expirar. É a armadilha nomeada
     * na B13, e aqui a resposta vem do banco justamente por isso.
     */
    papel: PapelSchema,
    carteira: CarteiraSchema,

    /**
     * O que a pessoa pode, assunto por assunto (ADR-0034, #732): as ações e até
     * onde valem, já com os ajustes dela. A tela esconde menus e botões por
     * aqui, e não pelo nome do papel; quem decide é a API, que confere a mesma
     * coisa a cada requisição. Todo assunto vem; o sem ação, com a lista vazia
     * e o alcance `nenhuma`.
     */
    permissoes: z.array(PermissaoDaSessaoSchema),

    escopo: z.object({
      unidade_id: IdSchema.nullable(),
      time_id: IdSchema.nullable(),
      inbox_ids: z.array(z.string()),
    }),

    /**
     * Flags do tenant (B19). Vem vazio até lá — e vazio é diferente de ausente:
     * o cliente já pode escrever `flags['x'] ?? false` sem esperar a task.
     */
    flags: z.record(z.string(), z.boolean()),

    /**
     * Esta organização é a de suporte da Cliqfy (#561): a tela de Feedback
     * aparece no menu. Ausente é falso, para quem fala com a API antiga.
     */
    suporte: z.boolean().optional(),

    /**
     * A pessoa lê a lista de espera de todas as organizações: a Operação
     * Cliqfy, ou quem administra uma organização interna da Cliqfy (06/10/2026).
     * A tela das Automações mostra "Quem pediu". Ausente é falso.
     */
    ve_lista_de_espera: z.boolean().optional(),

    /**
     * A pessoa tem a marca "acesso ao prontuário" (ADR-0022): a aba Prontuário
     * do cliente aparece. Ausente é falso.
     */
    acesso_ao_prontuario: z.boolean().optional(),

    /**
     * As configurações da REVENDA que moldam a interface (H18).
     *
     * Separado de `flags` de propósito: flag é recurso liberado após
     * homologação (princípio 08), com carimbo obrigatório no banco. Isto é
     * escolha da revenda, que ela liga e desliga em Configurações quando
     * quiser. Mesmo nome e ciclos de vida diferentes no mesmo lugar seria
     * confusão garantida.
     */
    preferencias: z.object({
      /**
       * Degrau 4 da escada (§5.2). Desligado, a ficha não mostra campo de CPF
       * ou CNPJ — e a API recusa gravar.
       *
       * A tela ESCONDE em vez de desabilitar: um campo cinza pergunta "por que
       * não posso?" a quem não tem como responder, porque quem liga isso é
       * quem administra a revenda, e não quem atende.
       */
      usar_documento_na_identidade: z.boolean(),
      /**
       * Agenda da clínica (#811/#643): mapa da finalidade-clínica → id da
       * operação no serviço de automações, que a tela de consulta usa (buscar
       * paciente, vagas, agendar, sincronizar). Vazio quando a clínica ainda não
       * foi configurada. Config da revenda, não dado de pessoa.
       */
      clinica: z.record(z.string(), z.string().uuid()).default({}),
      /** Os padrões do agendamento da consulta (#1187): com o que o formulário começa. */
      clinica_padroes: PadroesDaAgendaSchema.default({}),
    }),
  })
  .describe('Sessão da pessoa autenticada, com o tenant do vínculo');

export type Me = z.infer<typeof MeSchema>;

/**
 * O estado do segundo fator desta conta (B15).
 *
 * ── Por que uma rota própria, e não um campo em `/v1/me` ──────────────────
 *
 * Porque a resposta vem do provedor de identidade, por chamada externa, e o
 * `/v1/me` é caminho quente: ele roda a cada estabelecimento de sessão e a
 * cada renovação. Pendurar uma chamada de rede ali faria o login inteiro
 * depender da disponibilidade do Keycloak para responder algo que a tela
 * consulta uma vez.
 *
 * ── E por que não sai do token ────────────────────────────────────────────
 *
 * Porque o `amr` do token diz COMO a pessoa entrou nesta sessão, não o que a
 * conta dela tem. Quem cadastra o segundo fator com a sessão já aberta
 * continua com `amr: ['pwd']` até sair e entrar de novo — e a tela, lendo dali,
 * seguiria dizendo que a conta não tem segundo fator depois de ele existir.
 *
 * É a mesma armadilha que o `papel` deste arquivo já documenta: claim congela
 * na emissão. A diferença é que aqui a fonte não é o banco, é o provedor.
 */
export const SegurancaDaContaSchema = z
  .object({
    /**
     * A conta tem segundo fator? `null` = **não foi possível saber**.
     *
     * Três estados, e não dois, pelo mesmo motivo que `capacidades.ts` já
     * documenta: campo ausente vira "não sei", e "não sei" nunca vira uma
     * afirmação. Com booleano, o provedor fora do ar viraria `false` — e a
     * tela avisaria "sua conta não tem segundo fator" para alguém que tem.
     *
     * `null` faz o aviso NÃO aparecer. Um empurrão a menos durante uma
     * indisponibilidade é melhor do que um aviso falso, que ensina a ignorar
     * todos os outros.
     */
    segundoFator: z.boolean().nullable(),
    /** A revenda EXIGE segundo fator de quem tem este papel (decisão A2). */
    exigidoPelaRevenda: z.boolean(),
  })
  .describe('Estado do segundo fator, lido do provedor de identidade');

export type SegurancaDaConta = z.infer<typeof SegurancaDaContaSchema>;
